/* eslint-disable camelcase */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import {
  type GtKeyword,
  type GtKeywordCategory,
  type GtSpeaker,
  type GtTurn,
  identifyMeetingAttributionGtKeywords,
  type MeetingAttributionGtKeywordTurn,
} from '@/api/internal'

const MAX_CHUNK_TURNS = 40
const MAX_CHUNK_CODEPOINTS = 12_000
const MAX_CONCURRENCY = 3
const STORAGE_PREFIX = 'gt-keywords:v1:'

const storedKeywordSchema = z
  .object({
    turn_id: z.string(),
    phrase: z.string(),
    category: z.enum(['name', 'number', 'term', 'negation']),
    note: z.string().nullable(),
  })
  .strict()
const storedKeywordsSchema = z.record(z.string(), z.array(storedKeywordSchema))

export type GtKeywordStatus = 'idle' | 'running' | 'done' | 'error'

export interface GtKeywordProgress {
  done: number
  total: number
}

export interface GtKeywordCounts extends Record<GtKeywordCategory, number> {
  total: number
}

interface KeywordChunk {
  turns: MeetingAttributionGtKeywordTurn[]
  sourceTurnByRequestId: Record<string, string>
}

interface ChunkMutation {
  chunk: KeywordChunk
  signal: AbortSignal
}

const emptyCounts = (): GtKeywordCounts => ({ total: 0, name: 0, number: 0, term: 0, negation: 0 })

const storageKey = (meetingId: string): string => `${STORAGE_PREFIX}${meetingId}`

const splitIntoChunks = (turns: readonly GtTurn[], speakersById: ReadonlyMap<string, GtSpeaker>): KeywordChunk[] => {
  const chunks: KeywordChunk[] = []
  let current: KeywordChunk = { turns: [], sourceTurnByRequestId: {} }
  let codepointCount = 0

  const flush = () => {
    if (current.turns.length > 0) chunks.push(current)
    current = { turns: [], sourceTurnByRequestId: {} }
    codepointCount = 0
  }

  turns.forEach((turn, turnIndex) => {
    if (turn.state === 'deleted' || !turn.text.trim()) return
    const codepoints = Array.from(turn.text)
    const speaker = turn.speaker_id ? (speakersById.get(turn.speaker_id)?.name ?? null) : null
    for (let offset = 0, part = 0; offset < codepoints.length; part += 1) {
      const text = codepoints.slice(offset, offset + MAX_CHUNK_CODEPOINTS).join('')
      offset += MAX_CHUNK_CODEPOINTS
      if (current.turns.length >= MAX_CHUNK_TURNS || codepointCount + Array.from(text).length > MAX_CHUNK_CODEPOINTS) {
        flush()
      }
      const requestTurnId = `gtkw-${turnIndex}-${part}`
      current.turns.push({ turn_id: requestTurnId, speaker, text })
      current.sourceTurnByRequestId[requestTurnId] = turn.source_turn_id
      codepointCount += Array.from(text).length
    }
  })
  flush()
  return chunks
}

export const useGtKeywords = (meetingId: string) => {
  const [status, setStatus] = useState<GtKeywordStatus>('idle')
  const [progress, setProgress] = useState<GtKeywordProgress>({ done: 0, total: 0 })
  const [failedCount, setFailedCount] = useState(0)
  const [keywordsBySourceTurnId, setKeywordsBySourceTurnId] = useState<Record<string, GtKeyword[]>>({})
  const keywordsRef = useRef<Record<string, GtKeyword[]>>({})
  const failedChunksRef = useRef<KeywordChunk[]>([])
  const controllerRef = useRef<AbortController | null>(null)
  const operationRef = useRef(0)
  const mountedRef = useRef(false)

  const { mutateAsync } = useMutation({
    retry: 0,
    mutationFn: ({ chunk, signal }: ChunkMutation) =>
      identifyMeetingAttributionGtKeywords(meetingId, { turns: chunk.turns }, signal),
  })

  useEffect(() => {
    mountedRef.current = true
    keywordsRef.current = {}
    failedChunksRef.current = []
    setKeywordsBySourceTurnId({})
    setFailedCount(0)
    setProgress({ done: 0, total: 0 })
    setStatus('idle')

    try {
      const key = storageKey(meetingId)
      const stored = sessionStorage.getItem(key)
      if (stored !== null) {
        const parsed = storedKeywordsSchema.safeParse(JSON.parse(stored))
        if (parsed.success) {
          keywordsRef.current = parsed.data
          setKeywordsBySourceTurnId(parsed.data)
          setStatus('done')
        } else {
          sessionStorage.removeItem(key)
        }
      }
    } catch {
      try {
        sessionStorage.removeItem(storageKey(meetingId))
      } catch {
        // Storage may be unavailable in restricted browser contexts.
      }
    }

    return () => {
      mountedRef.current = false
      operationRef.current += 1
      controllerRef.current?.abort()
      controllerRef.current = null
    }
  }, [meetingId])

  const executeChunks = useCallback(
    async (chunks: KeywordChunk[], total: number, alreadyDone: number) => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      const operation = ++operationRef.current
      const failed: KeywordChunk[] = []
      let nextChunk = 0

      setStatus('running')
      setProgress({ done: alreadyDone, total })
      setFailedCount(0)

      const worker = async () => {
        while (nextChunk < chunks.length && !controller.signal.aborted) {
          const chunk = chunks[nextChunk]
          nextChunk += 1
          try {
            const response = await mutateAsync({ chunk, signal: controller.signal })
            if (!mountedRef.current || operationRef.current !== operation || controller.signal.aborted) return

            const merged = { ...keywordsRef.current }
            for (const keyword of response.data.keywords) {
              const sourceTurnId = chunk.sourceTurnByRequestId[keyword.turn_id]
              if (!sourceTurnId) continue
              const existing = merged[sourceTurnId] ?? []
              if (existing.some((item) => item.phrase === keyword.phrase)) continue
              merged[sourceTurnId] = [...existing, { ...keyword, turn_id: sourceTurnId }]
            }
            keywordsRef.current = merged
            setKeywordsBySourceTurnId(merged)
            try {
              sessionStorage.setItem(storageKey(meetingId), JSON.stringify(merged))
            } catch {
              // Storage is best-effort; the in-memory results remain available for this session.
            }
          } catch {
            if (controller.signal.aborted) return
            failed.push(chunk)
          }

          if (mountedRef.current && operationRef.current === operation && !controller.signal.aborted) {
            setProgress((current) => ({ ...current, done: current.done + 1 }))
          }
        }
      }

      await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENCY, chunks.length) }, worker))
      if (!mountedRef.current || operationRef.current !== operation || controller.signal.aborted) return

      failed.sort((left, right) => chunks.indexOf(left) - chunks.indexOf(right))
      failedChunksRef.current = failed
      setFailedCount(failed.length)
      setStatus(failed.length > 0 ? 'error' : 'done')
      controllerRef.current = null
    },
    [meetingId, mutateAsync]
  )

  const run = useCallback(
    (turns: readonly GtTurn[], speakersById: ReadonlyMap<string, GtSpeaker>) => {
      const chunks = splitIntoChunks(turns, speakersById)
      keywordsRef.current = {}
      setKeywordsBySourceTurnId({})
      failedChunksRef.current = []
      setFailedCount(0)
      try {
        sessionStorage.removeItem(storageKey(meetingId))
      } catch {
        // Storage is best-effort.
      }
      void executeChunks(chunks, chunks.length, 0)
    },
    [executeChunks, meetingId]
  )

  const retryFailed = useCallback(() => {
    const failed = failedChunksRef.current
    if (failed.length === 0) return
    void executeChunks(failed, progress.total, progress.total - failed.length)
  }, [executeChunks, progress.total])

  const counts = Object.values(keywordsBySourceTurnId)
    .flat()
    .reduce<GtKeywordCounts>((result, keyword) => {
      result.total += 1
      result[keyword.category] += 1
      return result
    }, emptyCounts())

  return { status, progress, failedCount, keywordsBySourceTurnId, counts, run, retryFailed }
}
